/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Formik, FieldArray, yupToFormErrors } from 'formik'
import { TextArea } from '@mdsol/lego'
import Modal from '../../../../components/Modal'
import DocTypeDropdown from '../../../../components/pickers/DocTypeDropdown'
import { Select } from '../../../../components/Select/Select'
import UserGroupSteps from '../../../../components/workflow/UserGroupSteps'
import { formatTextAreaCounter, transformToSelectOptions } from '../../../../utils/formHelpers'
import ValidationSchema from './FormValidationForAddNewPlaceholder'
import { ErrorMessageStyled } from '~/components/shared/ErrorMessage'
import Field from '~/components/shared/Field'
import AddCustomJustificationModal from '../../../../components/modals/AddCustomJustificationModal'
import { useWorkflowCreation } from '../../../../hooks/useWorkflowCreation'
import { getFolderFolderType, getFileCRApprovers, getFileApprovers } from '../../../../services/api/files'
import { getJustifications } from '../../../../services/api/api.settings'
import { toast } from '../../../../services/toast'
import { documentTypesApiService } from '../../../../services/api/api.documenttypes'
import { changeRequestTypesApiService } from '../../../../services/api/api.changerequesttypes'
import { WorkflowType } from '../../../../constants/workflow'
import { SystemFolderType } from '../../../../constants/fsi'
import Constants from '../../../../constants/constants'
import type { SelectOption, DocumentTypeObject, WorkflowTypeData } from './AddDocumentModal.types'
import { useEsignature } from '~/hooks/useEsignature'
import {
  assembleFsiCRApproversForServer,
  assembleFsiEditorsForServer,
  assembleFsiApproversForServer,
  assembleFsiReviewersForServer
} from '../../../../services/api/files'
import useWorkflowServiceNew from '~/services/workflow/useWorkflowServiceNew'

/**
 * Add New Placeholder Modal Component
 * Migrated from lego/page/browser/add-document/add-new-document/AddNewPlaceholder.jsx
 *
 * Creates a placeholder document in a folder with workflow approval process.
 * Used when a document is expected but not yet available.
 */

interface AddNewPlaceholderModalProps {
  parentFsi: {
    id: number
    name: string
    systemFolderType?: string
    templateId?: number
  }
  onClose?: () => void
}

interface FormValues {
  crdoctype: SelectOption | null
  fileName: string
  justificationTextId: number | null
  description: string
  selectedDocTypeObj: DocumentTypeObject
  typeData?: WorkflowTypeData
}

interface RequestData {
  justificationTextId: SelectOption | null
  approver: Record<string, unknown>
  parentFolderName?: string
  description?: string
  customJustificationText?: string
  documentTypeTemplateId?: SelectOption
}

export const AddNewPlaceholderModal: React.FC<AddNewPlaceholderModalProps> = (props) => {
  const { t: translate } = useTranslation()
  const [isOpen, setIsOpen] = useState(true)
  const [availableJustifications, setAvailableJustifications] = useState<SelectOption[]>([])
  const [allowedDocTypes, setAllowedDocTypes] = useState<number[] | null>(null)
  const [showAddCustomModal, setShowAddCustomModal] = useState(false)
  const [request, setRequest] = useState<RequestData>({
    justificationTextId: null,
    approver: {}
  })
  const validationSchema = ValidationSchema()
  const [selectedDocTypeObj, setSelectedDocTypeObj] = useState<DocumentTypeObject>({} as DocumentTypeObject)
  const [initialFormValues, setInitialFormValues] = useState<FormValues>({
    crdoctype: null,
    fileName: '',
    justificationTextId: null,
    description: '',
    selectedDocTypeObj: {} as DocumentTypeObject
  })
  const [descriptionPlaceHolder, setDescriptionPlaceHolder] = useState('')
  const ref = useRef<{ fileName?: string; description?: string }>({})
  const formikRef = useRef<any>(null)

  // Remove useWorkflowCreation; use direct API call and autorunTaskApprovals
  const workflowService = useWorkflowServiceNew()

  const [isCreating, setIsCreating] = useState(false)

  useEffect(() => {
    const initializeModal = async () => {
      const requestData = { ...request }

      if (!props?.parentFsi?.id) {
        doClose()
        return
      }

      requestData.parentFolderName = props.parentFsi.name

      // Load folder type information
      if (props.parentFsi.systemFolderType === SystemFolderType.REGULATED_CONTENT || props.parentFsi.templateId) {
        try {
          const folderTypeData = await getFolderFolderType(props.parentFsi.id)
          assembleAllowedDocTypes(folderTypeData as any)
        } catch (error) {
          console.error('Error loading folder type:', error)
        }
      } else {
        assembleAllowedDocTypes({
          documentTypesAllowed: Constants.system.settings.folderType.documentTypesAllowed.ALL
        })
      }

      // Set default description
      requestData.description = translate('CR_DESC_DEFAULT_PLACEHOLDER_ADD_PLACEHOLDER')
      setDescriptionPlaceHolder(requestData.description)
      setRequest(requestData)
      setInitialFormValues((oldData: FormValues) => ({
        ...oldData,
        description: requestData.description || ''
      }))

      // Load justifications
      try {
        const lang = localStorage.getItem('appLanguage') || 'en-us'
        const data = await getJustifications(lang)
        const justificationsData = (data as any).elements || []
        justificationsData.push({ id: 0, text: translate('CUSTOM...') })
        setAvailableJustifications(transformToSelectOptions(justificationsData, 'id', 'text'))
      } catch (error) {
        console.error('Error loading justifications:', error)
      }
    }

    initializeModal()
  }, [props.parentFsi, translate])

  const assembleAllowedDocTypes = (folderTypeData: any) => {
    let allowedDocTypesData: number[] | null = null
    if (folderTypeData?.documentTypesAllowed !== Constants.system.settings.folderType.documentTypesAllowed.ALL) {
      allowedDocTypesData = folderTypeData?.allowedDocumentTypeTemplateIds
    }
    setAllowedDocTypes(allowedDocTypesData)
  }

  const docTypeChanged = async (selectedDocType: SelectOption | null) => {
    if (!selectedDocType?.value) return

    setInitialFormValues((oldData: FormValues) => ({
      ...oldData,
      crdoctype: selectedDocType,
      fileName: ref.current?.fileName || '',
      description: ref.current?.description || descriptionPlaceHolder || ''
    }))
    setRequest((old) => ({ ...old, documentTypeTemplateId: selectedDocType }))

    try {
      const data = await documentTypesApiService.getById(Number(selectedDocType.value), false)
      const selectedDocTypeObjData = data as DocumentTypeObject
      setSelectedDocTypeObj(selectedDocTypeObjData)
      await downloadAllTaskGroupSteps(selectedDocTypeObjData)
    } catch (error) {
      console.error('Error loading document type:', error)
    }
  }

  const downloadAllTaskGroupSteps = async (selectedDocTypeObjData: DocumentTypeObject) => {
    const typeData: any = {}

    if (selectedDocTypeObjData?.workflowTemplate?.id) {
      try {
        const data = await changeRequestTypesApiService.get(selectedDocTypeObjData.workflowTemplate.id)
        const assembledTypeData = await changeRequestTypesApiService.assembleForUI(data, true)
        setInitialFormValues((oldData: FormValues) => ({
          ...oldData,
          typeData: assembledTypeData
        }))
      } catch (error) {
        console.error('Error loading workflow template:', error)
        doClose()
      }
    } else {
      try {
        const [crApprovers, approvers] = await Promise.all([
          getFileCRApprovers(props.parentFsi.id, true),
          getFileApprovers(props.parentFsi.id)
        ])
        typeData.initialApprovers = crApprovers.initialApprovers
        typeData.approvers = approvers.approvers
        setInitialFormValues((oldData: FormValues) => ({
          ...oldData,
          typeData: typeData
        }))
      } catch (error) {
        console.error('Error loading approvers:', error)
      }
    }
  }

  const save = async (values: FormValues) => {
    console.log('AddNewPlaceholderModal save called', values)
    if (isCreating) {
      console.log('Already creating, skipping')
      return
    }
    if (!formikRef.current) {
      console.error('formRef.current is null')
      return
    }
    try {
      setIsCreating(true)
      console.log('Validating form...')
      const errors = await formikRef.current.validateForm()
      console.log('Validation errors:', errors)
      if (Object.keys(errors || {}).length > 0) {
        console.log('Validation failed')
        toast.error(translate('PLEASE_FIX_VALIDATION_ERRORS'))
        setIsCreating(false)
        return
      }
      console.log('No validation errors, proceeding with submission')
      // Assemble request object
      const req: any = {
        initialApprovers: null,
        approvers: null,
        description: values.description,
        fileName: values.fileName,
        documentTypeTemplateId: values.crdoctype?.value,
        parentFolderId: props.parentFsi.id,
        workflowType: WorkflowType.CREATE_PLACEHOLDER,
        justificationTextId: null,
        customJustificationText: null
      }
      if (typeof values.justificationTextId === 'number') {
        if (values.justificationTextId === 0) {
          req.justificationTextId = null
          req.customJustificationText = request.customJustificationText
        } else {
          req.justificationTextId = values.justificationTextId
          req.customJustificationText = null
        }
      }
      // Approvers logic (parity with legacy)
      const typeData = values.typeData
      if (typeData?.initialApprovers?.type === 'SETUP_WITH_WORKFLOW') {
        req.initialApprovers = assembleFsiCRApproversForServer(typeData.initialApprovers)
      }
      if (typeData?.approvers?.type === 'SETUP_WITH_WORKFLOW') {
        req.approvers = assembleFsiApproversForServer(typeData.approvers)
      }

      let initialApproversData = []
      if (typeData?.initialApprovers?.type === 'SETUP_WITH_WORKFLOW') {
        req.initialApprovers = assembleFsiCRApproversForServer(typeData).initialApprovers.steps
        initialApproversData = req.initialApprovers
      } else if (typeData?.initialApprovers?.type === 'USE_PREDEFINED') {
        initialApproversData = assembleFsiCRApproversForServer(typeData).initialApprovers.steps
      }
      if (typeData?.approvers?.type === 'SETUP_WITH_WORKFLOW') {
        req.approvers = assembleFsiApproversForServer(typeData).approvers.steps
      }

      // Call requestDocumentChange API
      const { requestDocumentChange } = await import('../../../../services/api/workflows')
      const { getById } = await import('../../../../services/api/workflows')
      const response = await requestDocumentChange(req)
      if (response?.warning) {
        toast.info(response.warning)
      }
      doClose()
      if (response.workflowStatus === 'FINISHED') {
        window.dispatchEvent?.(new CustomEvent('updateFiles'))
        doClose()
        setIsCreating(false)
        return
      }
      // Fetch workflow and run autorunTaskApprovals
      const wf = await getById(response.id)

      // autorunTaskApprovals errors should not block success message (parity with legacy)
      try {
        await workflowService.autorunTaskApprovals(wf)
      } catch (autorunError) {
        // Non-blocking error - don't prevent success message
      }

      toast.success(translate('BROWSER_ADD_FILE_SUCCESS_MSG', { workflowNo: wf.workflowNo }))
    } catch (error: any) {
      console.error('Failed to create placeholder:', error)
      toast.error(error?.body?.message || error?.message || translate('ERROR_OCCURRED'))
    } finally {
      setIsCreating(false)
    }
  }

  const doClose = () => {
    setIsOpen(false)
    if (props.onClose) {
      props.onClose()
    }
  }

  if (!isOpen) return null

  return (
    <Modal
      show={isOpen}
      handleClose={doClose}
      size="lg"
      className="add-doc-modal-wrapper theme-sandman"
      title={translate('BROWSER_ADD_PLACEHOLDER_H_TITLE')}
    >
      <Formik
        initialValues={{ ...initialFormValues, selectedDocTypeObj }}
        validationSchema={validationSchema}
        onSubmit={save}
        enableReinitialize
        innerRef={(formRef) => {
          formikRef.current = formRef
        }}
      >
        {({ values, setFieldValue, handleSubmit }) => {
          return (
            <>
              <Modal.Body>
                <div className="form-group doc-type-dropdown-wrapper">
                  <label htmlFor="de60f3db-8d2a-4b34-9ca5-f181ffcb3f23">{translate('DOC_TYPE')}</label>
                  <DocTypeDropdown
                    name="crdoctype"
                    isSingle={true}
                    required={true}
                    id="525091e3-64a0-411c-845e-2120ccaa8696"
                    allowedTypes={allowedDocTypes || undefined}
                    onChange={(option) => {
                      docTypeChanged(option)
                      setFieldValue('crdoctype', option)
                    }}
                  />
                  <ErrorMessageStyled name="crdoctype" />
                </div>

                {selectedDocTypeObj?.requireJustification && (
                  <div className="form-group w50p">
                    <label htmlFor="i-jtype">{translate('JUSTIFICATION')}</label>
                    <div>
                      <Select
                        id="i-jtype"
                        name="justificationTextId"
                        placeholder={translate('SELECT_JUSTIFICATION_LABEL')}
                        isClearable={false}
                        value={values.justificationTextId}
                        options={availableJustifications}
                        onChange={(option: any) => {
                          setFieldValue('justificationTextId', option ? option.value : null)
                          setRequest((previousData) => ({
                            ...previousData,
                            justificationTextId: option
                          }))
                        }}
                      />
                      <ErrorMessageStyled name="justificationTextId" />
                    </div>
                    {values.justificationTextId === 0 && (
                      <button
                        type="button"
                        className="btn btn-secondary mt10"
                        onClick={() => setShowAddCustomModal(true)}
                      >
                        {translate('DESCRIPTION')}
                      </button>
                    )}
                  </div>
                )}

                <div className="form-group">
                  <label htmlFor="in-f">{translate('BROWSER_SEND_RQST_REASON_LABEL')}</label>
                  <div className="w50p">
                    <TextArea
                      maxLength={254}
                      name="description"
                      onChange={(event: any) => {
                        const { value } = event.target
                        ref.current.description = value
                        setFieldValue('description', value)
                      }}
                      id="in-f"
                      value={values.description}
                      rows={6}
                    />
                    <ErrorMessageStyled name="description" />
                  </div>
                </div>

                <div className="form-group w50p">
                  <label>{translate('BROWSER_ADD_PLACEHOLDER_FILENAME_LABEL')}</label>
                  <div>
                    <Field
                      type="text"
                      name="fileName"
                      onChange={(event: any) => {
                        const { value } = event.target
                        ref.current.fileName = value
                        setFieldValue('fileName', value)
                      }}
                      maxLength={234}
                      id="d4893d04-3cf6-4fcb-8d9c-8e066f77f15e"
                      style={{ width: '-webkit-fill-available' }}
                    />
                    <ErrorMessageStyled name="fileName" />
                  </div>
                </div>

                {values?.typeData?.initialApprovers?.type === 'SETUP_WITH_WORKFLOW' && (
                  <div>
                    <FieldArray name="typeData.initialApprovers.steps">
                      {(arrayHelpers) => (
                        <UserGroupSteps
                          arrayHelpers={arrayHelpers}
                          values={values.typeData?.initialApprovers?.steps || []}
                          nofallTxt="BROWSER_FILE_SETTINGS_CR_APPROVERS_INFO_LABEL"
                          labelTxt="BROWSER_FILE_SETTINGS_CR_APPROVERS_APPROVAL_SEQUENCE_HEADLINE"
                          increaseUserGroupWidth={true}
                        />
                      )}
                    </FieldArray>
                  </div>
                )}

                {values?.typeData?.approvers?.type === 'SETUP_WITH_WORKFLOW' && (
                  <div>
                    <FieldArray name="typeData.approvers.steps">
                      {(arrayHelpers) => (
                        <UserGroupSteps
                          arrayHelpers={arrayHelpers}
                          values={values.typeData?.approvers?.steps || []}
                          nofallTxt="BROWSER_FILE_SETTINGS_APPROVERS_INFO_LABEL"
                          labelTxt="BROWSER_FILE_SETTINGS_APPROVERS_APPROVAL_SEQUENCE_HEADLINE"
                          increaseUserGroupWidth={true}
                        />
                      )}
                    </FieldArray>
                  </div>
                )}
              </Modal.Body>

              <Modal.Footer>
                <button className="btn btn-close btn-link" type="button" onClick={doClose}>
                  {translate('CANCEL')}
                </button>
                <button className="btn btn-primary" type="button" onClick={() => handleSubmit()} disabled={isCreating}>
                  {isCreating ? translate('SENDING...') : translate('BROWSER_SEND_REQUEST')}
                </button>
              </Modal.Footer>

              {showAddCustomModal && (
                <AddCustomJustificationModal
                  request={request}
                  setRequest={setRequest}
                  setShowAddCustomModal={setShowAddCustomModal}
                />
              )}
            </>
          )
        }}
      </Formik>
    </Modal>
  )
}

export default AddNewPlaceholderModal
